import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";

import { useRequestState } from "@app/hooks";
import { Strings } from "@app/constants";
import { FormProvider, RHFTextField } from "@app/components";
import { addBrand } from "../slice";
import { addBrandSchema } from "../validations";
import { selectAddBrandName } from "../selector";
import { IBrandData } from "../types";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material/styles";

interface AddBrandModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  selectedBrandData: IBrandData | null;
}

const AddBrandModal: React.FC<AddBrandModalProps> = ({
  open,
  onClose,
  selectedBrandData,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [selectedImage, setSelectedImage] = useState<File | string | null>(
    null
  );
  const methods = useForm<any>({
    resolver: yupResolver(addBrandSchema),
    defaultValues: {
      carBrandName: selectedBrandData ? selectedBrandData.name : "",
    },
  });

  useEffect(() => {
    if (selectedBrandData) {
      setSelectedImage(selectedBrandData.image);
    }
  }, [selectedBrandData]);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const onSubmit = (form: any) => {
    try {
      dispatch(
        addBrand({
          name: form.carBrandName,
          image: selectedImage,
          selectedBrandId: selectedBrandData ? selectedBrandData?.id : null,
        })
      );
    } catch (error) {
      console.error("Add Brand failed:", error);
    }
  };

  const onSuccess = useCallback(() => {
    methods.reset();
    setSelectedImage(null);
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { loading } = useRequestState({
    stateSelector: selectAddBrandName,
    errorShown: true,
    successMessageShown: true,
    onSuccess: onSuccess,
  });

  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <Modal open={open} onClose={onClose}>
      <FormProvider methods={methods}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 500, md: 600 },
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: { xs: 2, sm: 4 },
          }}
        >
          <Stack spacing={3}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Typography
                fontFamily={fontFamily}
                fontWeight={500}
                color="#000000"
              >
                {Strings.button.addBrand}
              </Typography>
              <Icon
                icon="material-symbols-light:close"
                width={24}
                onClick={onClose}
                style={{ cursor: "pointer", color: "#080808" }}
              />
            </Stack>

            <RHFTextField
              name="carBrandName"
              fullWidth
              size="small"
              placeholder="Name"
            />

            <Box
              sx={{
                height: 140,
                width: { xs: "100%", sm: "60%", md: "40%" },
                border: "1px solid #1E91B6",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#F7F9FB",
                cursor: "pointer",
                overflow: "hidden",
                position: "relative",
              }}
              onClick={() => !selectedImage && fileInputRef.current?.click()}
            >
              {selectedImage ? (
                <>
                  <img
                    src={
                      selectedImage instanceof File
                        ? URL.createObjectURL(selectedImage)
                        : selectedImage
                    }
                    alt="Brand Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />

                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      background: "rgba(255,255,255,0.9)",
                      width: 34,
                      height: 34,
                      boxShadow: 2,
                      "&:hover": { background: "#fff" },
                    }}
                  >
                    <Icon icon="solar:refresh-bold" width={20} />
                  </IconButton>
                </>
              ) : (
                <Stack alignItems="center" spacing={1}>
                  <Icon
                    icon="fluent:camera-add-48-filled"
                    width={40}
                    color="#1E91B6"
                  />
                  <Typography>Upload Brand Logo</Typography>
                </Stack>
              )}
            </Box>

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedImage(file);
                }
              }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="flex-end"
              spacing={2}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  borderRadius: 1,
                  width: { xs: "100%", sm: "28%" },
                  height: 55,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  fontFamily: { fontFamily },
                  fontWeight: 700,
                }}
              >
                {Strings.button.close}
              </Button>
              <LoadingButton
                variant="contained"
                onClick={methods.handleSubmit(onSubmit)}
                sx={{
                  borderRadius: 1,
                  width: { xs: "100%", sm: "28%" },
                  height: 55,
                  backgroundColor: theme.palette.primary.main,
                  fontFamily: { fontFamily },
                  fontWeight: 700,
                  color: theme.palette.common.white,
                }}
                loading={loading}
              >
                {Strings.button.send}
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      </FormProvider>
    </Modal>
  );
};

export { AddBrandModal };
